/* eslint-disable react/jsx-curly-newline */
/* eslint-disable function-paren-newline */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo } from 'react';
import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertAdapter, DataTableCellDeleteButton, DataTableCellEditButton } from 'pg-ui-library';

import { useTypedSelector } from 'src/hooks/useTypedSelector';
import EmployeesService, { IEmployeeBaseParams } from 'src/services/employees.service';

import { INote } from 'src/interfaces';
import { DefaultTable } from 'src/components';

const customStyles = {
  headRow: {
    style: {
      border: 'none',
    },
  },
  headCells: {
    style: {
      color: '#9399B2',
      font: 'normal normal bold 16px Biotif',
    },
  },
  rows: {
    style: {
      font: 'normal normal normal 14px Biotif',
      color: '#1B1D35',
      borderRadius: '5px',
      padding: '16px',
      '&:not(:last-of-type)': {
        borderBottomWidth: '0px',
      },
    },
    stripedStyle: {
      color: '#1B1D35',
      backgroundColor: '#F3F4F8',
    },
    highlightOnHoverStyle: {
      backgroundColor: 'rgb(230, 244, 244)',
      borderBottomColor: '#FFFFFF',
      borderRadius: '25px',
      outline: '1px solid #FFFFFF',
    },
  },
  cells: {
    style: {
      justifyContent: 'left',
      width: '150px',
      '&:nth-of-type(1)': {
        marginLeft: '-15px',
      },
      '&:nth-of-type(2)': {
        marginLeft: '-1px',
      },
      '&:nth-of-type(3)': {
        justifyContent: 'right',
        maxWidth: '20px',
      },
      '&:nth-of-type(4)': {
        justifyContent: 'right',
        paddingRight: '36px',
        maxWidth: '20px',
      },
    },
  },
  pagination: {
    style: {
      border: 'none',
    },
  },
};

const editButtonWrapper = (onClick: Function) => <DataTableCellEditButton onClick={() => onClick()} />;

export const NotesTable: React.FC<{ onEdit: Function }> = ({ onEdit }) => {
  const queryClient = useQueryClient();
  const { selectedEmployee } = useTypedSelector(state => state.employees.data);
  const { config } = useTypedSelector(state => state.config.data);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const companyId = config?.selectedCompanyId || '';
  const employeeId = selectedEmployee.employee?.id || '';

  const deleteParams: IEmployeeBaseParams = { companyId, employeeId };

  const { isLoading, data, refetch } = useQuery(['notes'], () =>
    EmployeesService.getEmployeeNotes({
      page,
      companyId,
      limit,
      employeeId,
    })
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerRowsChange = async (newLimit: number, newPage: number) => {
    setLimit(newLimit);
    setPage(newPage);
  };

  const mutation = useMutation((note: INote) => EmployeesService.deleteEmployeeNote(note, deleteParams), {
    onSuccess: () => {
      queryClient.invalidateQueries(['notes']);
    },
    onError: () => {
      AlertAdapter('Failed to delete', 'error');
    },
  });

  const deleteButtonWrapper = (onClick: Function) =>
    !mutation.isLoading && <DataTableCellDeleteButton onClick={() => onClick()} />;

  const deleteNote = (note: INote) => {
    mutation.mutate(note);
  };

  useEffect(() => {
    refetch();
  }, [page, limit]);

  const columnsAndActions = useMemo(
    () => [
      {
        name: 'Date',
        selector: (row: any) => row.createdAt,
        sortable: true,
        sortField: 'date',
        width: '120px',
      },
      {
        name: 'Notes',
        selector: (row: any) => row.note,
      },

      {
        cell: (row: INote) => editButtonWrapper(() => onEdit(row)),
      },
      {
        cell: (row: INote) => deleteButtonWrapper(() => deleteNote(row)),
      },
    ],
    []
  );

  return (
    <DefaultTable
      customStyles={customStyles}
      columns={columnsAndActions}
      data={data?.data.items || []}
      progressPending={isLoading}
      pagination
      paginationServer
      paginationTotalRows={data?.data.meta.totalItems}
      onChangeRowsPerPage={handlePerRowsChange}
      onChangePage={handlePageChange}
    />
  );
};
